import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

export interface MediaSource {
  type: 'youtube' | 'bandcamp';
  id: string;
  title: string;
  artistName?: string;
}

interface MediaPlayerState {
  currentMedia: MediaSource | null;
  isPlaying: boolean;
}

interface MediaPlayerContextValue extends MediaPlayerState {
  registerPlaying: (source: MediaSource) => void;
  registerStopped: (id: string) => void;
  setPlayState: (id: string, playing: boolean) => void;
  pause: () => void;
  play: () => void;
  stop: () => void;
  registerControls: (id: string, controls: EmbedControls) => void;
  unregisterControls: (id: string) => void;
}

export interface EmbedControls {
  pause: () => void;
  play: () => void;
  stop: () => void;
}

const MediaPlayerContext = createContext<MediaPlayerContextValue | null>(null);

export function MediaPlayerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MediaPlayerState>({
    currentMedia: null,
    isPlaying: false,
  });

  const controlsRef = useRef<Map<string, EmbedControls>>(new Map());
  // Track which media we're currently trying to stop to prevent re-entrancy
  const stoppingRef = useRef<string | null>(null);

  const registerPlaying = useCallback((source: MediaSource) => {
    console.log('[media] registerPlaying called with:', source);
    setState(prev => {
      // If something else is playing, we need to stop it
      // But do it AFTER this setState completes to avoid side effects during render
      if (prev.currentMedia && prev.currentMedia.id !== source.id) {
        const oldId = prev.currentMedia.id;
        // Use setTimeout to defer the stop call outside of setState
        if (stoppingRef.current !== oldId) {
          stoppingRef.current = oldId;
          setTimeout(() => {
            const oldControls = controlsRef.current.get(oldId);
            console.log('[media] Deferred stop for:', oldId, 'controls:', !!oldControls);
            oldControls?.stop();
            stoppingRef.current = null;
          }, 0);
        }
      }
      return { currentMedia: source, isPlaying: true };
    });
  }, []);

  const registerStopped = useCallback((id: string) => {
    setState(prev => {
      if (prev.currentMedia?.id === id) {
        return { currentMedia: null, isPlaying: false };
      }
      return prev;
    });
  }, []);

  const setPlayState = useCallback((id: string, playing: boolean) => {
    setState(prev => {
      if (prev.currentMedia?.id === id) {
        return { ...prev, isPlaying: playing };
      }
      return prev;
    });
  }, []);

  const registerControls = useCallback((id: string, controls: EmbedControls) => {
    controlsRef.current.set(id, controls);
  }, []);

  const unregisterControls = useCallback((id: string) => {
    controlsRef.current.delete(id);
  }, []);

  const pause = useCallback(() => {
    if (state.currentMedia) {
      const controls = controlsRef.current.get(state.currentMedia.id);
      controls?.pause();
      setState(prev => ({ ...prev, isPlaying: false }));
    }
  }, [state.currentMedia]);

  const play = useCallback(() => {
    if (state.currentMedia) {
      const controls = controlsRef.current.get(state.currentMedia.id);
      controls?.play();
      setState(prev => ({ ...prev, isPlaying: true }));
    }
  }, [state.currentMedia]);

  const stop = useCallback(() => {
    if (state.currentMedia) {
      const controls = controlsRef.current.get(state.currentMedia.id);
      controls?.stop();
      setState({ currentMedia: null, isPlaying: false });
    }
  }, [state.currentMedia]);

  return (
    <MediaPlayerContext.Provider
      value={{
        ...state,
        registerPlaying,
        registerStopped,
        setPlayState,
        pause,
        play,
        stop,
        registerControls,
        unregisterControls,
      }}
    >
      {children}
    </MediaPlayerContext.Provider>
  );
}

export function useMediaPlayer() {
  const context = useContext(MediaPlayerContext);
  if (!context) {
    throw new Error('useMediaPlayer must be used within MediaPlayerProvider');
  }
  return context;
}

export function useMediaPlayerOptional() {
  return useContext(MediaPlayerContext);
}
